import PropTypes from 'prop-types';
import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Location } from './location';
import styled from '@emotion/styled';

export const ConfirmationSection = styled.div`
  font-size: 16px;
  line-height: 1.6;
  margin-top: 24px;
  margin-bottom: 24px;
`;

const ConfirmationPending = (props) => {
  const intl = useIntl();
  const {
    guide_full_name,
    has_resource,
    resource_name,
    custom_body,
    location,
    custom_locations_enabled,
    online,
    paid_in_cash,
    show_cc_info,
    custom_title,
    footer_content,
    isBookingAffiliate,
  } = props;

  const renderCreditCardInfo = () => {
    if (!paid_in_cash && show_cc_info) {
      return (
        <ConfirmationSection>
          <FormattedMessage
            defaultMessage="Your credit card will not be charged until that time."
            description="Message explaining that credit card won't be charged until booking is confirmed"
          />
        </ConfirmationSection>
      );
    }
  };

  const renderDefaultTitleText = () => {
    return (
      <div>
        <h1>
          <span className="titleRow" data-testid="title-row">
            <FormattedMessage
              defaultMessage="Your request has been successfully submitted."
              description="Success message for booking request submission"
            />
          </span>
        </h1>
      </div>
    );
  };

  const renderCustomTitleText = (customCopy) => {
    return (
      <h1>
        <div
          className="titleRow contains-brand-link"
          dangerouslySetInnerHTML={{ __html: customCopy } /* // nosemgrep */}
        />
      </h1>
    );
  };

  const renderDefaultBodyText = () => {
    const name = has_resource ? resource_name : guide_full_name;

    return (
      <div className="Confirmation-Body">
        <h3>
          <FormattedMessage
            defaultMessage="We're just as excited as you are..."
            description="Enthusiastic message for pending booking"
          />
        </h3>
        <ConfirmationSection>
          <FormattedMessage
            defaultMessage="However, please note that this is <strong>not a confirmed booking</strong> until it has been accepted by {name}."
            description="Message explaining booking is pending confirmation until accepted by host or resource"
            values={{
              name,
              strong: (chunks) => <strong>{chunks}</strong>,
            }}
          />
        </ConfirmationSection>

        {renderCreditCardInfo()}
        {custom_locations_enabled && !online && (
          <Location location={location} />
        )}
        {custom_body && (
          <ConfirmationSection
            className="contains-brand-link"
            dangerouslySetInnerHTML={{ __html: custom_body }}
          />
        )}

        <hr className="separator" />
        <h3>
          <FormattedMessage
            defaultMessage="What happens next"
            description="Section heading for next steps"
          />
        </h3>
        <p>
          <FormattedMessage
            defaultMessage="We will let you know via email as soon as the booking is confirmed."
            description="Message about email notification for booking confirmation"
          />
        </p>
      </div>
    );
  };

  const renderAffiliateText = () => {
    return (
      <div className="Confirmation-Body">
        <ConfirmationSection>
          <FormattedMessage
            defaultMessage="This is not a confirmed booking yet. {name} will review your request and respond via email."
            description="Message for affiliate bookings pending confirmation"
            values={{ name: guide_full_name }}
          />
        </ConfirmationSection>

        <hr className="separator" />
        <h3>
          <FormattedMessage
            defaultMessage="What happens next"
            description="Section heading for next steps"
          />
        </h3>
        <p>
          <FormattedMessage
            defaultMessage="You will receive an email confirmation once your booking has been reviewed."
            description="Email notification message for affiliate bookings"
          />
        </p>
      </div>
    );
  };

  return (
    <div className="Confirmation-Body">
      <div className="Confirmation-Title" data-testid="confirmation-title">
        {custom_title
          ? renderCustomTitleText(custom_title)
          : renderDefaultTitleText()}
      </div>
      {!isBookingAffiliate ? renderDefaultBodyText() : renderAffiliateText()}
      {footer_content && (
        <>
          <hr className="separator" />
          {footer_content}
        </>
      )}
    </div>
  );
};

ConfirmationPending.displayName = 'ConfirmationPending-Plugins';

ConfirmationPending.propTypes = {
  guide_full_name: PropTypes.string.isRequired,
  footer_content: PropTypes.node,
  custom_title: PropTypes.string,
  custom_body: PropTypes.string,
  paid_in_cash: PropTypes.bool,
  show_cc_info: PropTypes.bool,
  has_resource: PropTypes.bool,
  resource_name: PropTypes.string,
  location: PropTypes.object,
  custom_locations_enabled: PropTypes.bool,
  online: PropTypes.bool,
  isBookingAffiliate: PropTypes.bool,
};

export default ConfirmationPending;
